import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Building2, Hammer, Wrench, PlusSquare, Maximize2, Shield,
  FileText, IndianRupee, HardHat, LayoutGrid, ArrowRight, Check, PencilRuler
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { services, contractModels } from "../data/data";

const iconMap = {
  Building2, Hammer, Wrench, PlusSquare, Maximize2, Shield,
  FileText, IndianRupee, HardHat, LayoutGrid, PencilRuler
};

// Page Header
const PageHeader = () => (
  <section className="bg-pc-primary pt-32 pb-20 texture-grain">
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
    >
      <div className="flex items-center gap-3 mb-5">
        <span className="w-10 h-[2px] bg-pc-accent" />
        <span className="text-pc-accent font-heading text-sm font-semibold uppercase tracking-[0.15em]">
          Our Services
        </span>
      </div>
      <h1 className="font-heading text-3xl md:text-4xl lg:text-[52px] font-extrabold text-white leading-[1.1] tracking-tight mb-5">
        What We Build
      </h1>
      <p className="font-body text-white/50 text-lg max-w-2xl leading-relaxed">
        From new construction to structural strengthening, we handle all aspects of
        civil construction with the same attention to quality and detail.
      </p>
    </motion.div>
  </section>
);

// Service Detail Cards
const ServiceDetails = () => (
  <section className="bg-white py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-5">
        {services.map((service) => {
          const Icon = iconMap[service.icon];
          return (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className="rounded-xl border border-pc-border/60 overflow-hidden hover:shadow-xl card-accent-hover transition-shadow duration-300 bg-white"
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                <div className="p-8 lg:border-r border-pc-border/50 bg-pc-light/50">
                  <div className="w-14 h-14 rounded-xl bg-pc-accent/8 flex items-center justify-center mb-5">
                    {Icon && <Icon className="w-7 h-7 text-pc-accent" />}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-pc-primary mb-2">
                    {service.title}
                  </h3>
                  <p className="font-body text-sm text-pc-muted leading-relaxed">
                    {service.shortDescription}
                  </p>
                </div>
                <div className="p-8 lg:col-span-2">
                  <p className="font-body text-pc-muted text-[15px] leading-[1.75] mb-6">
                    {service.fullDescription}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <Check className="w-4 h-4 text-pc-accent flex-shrink-0" />
                        <span className="font-body text-sm text-pc-text">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

// Contract Models
const ContractModelsSection = () => (
  <section className="bg-pc-light py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6 }}
        className="max-w-xl mb-16"
      >
        <div className="accent-bar mb-5" />
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-pc-primary tracking-tight mb-4">
          Contract Models
        </h2>
        <p className="font-body text-pc-muted text-base leading-relaxed">
          We offer different contract models to suit the nature of your project
          and your comfort level with construction management.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {contractModels.map((model, index) => {
          const Icon = iconMap[model.icon];
          return (
            <motion.div
              key={model.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                className="bg-white border-pc-border/60 hover:shadow-xl card-accent-hover transition-shadow duration-300 rounded-xl h-full"
              >
                <CardContent className="p-7">
                  <div className="w-12 h-12 rounded-lg bg-pc-primary/5 flex items-center justify-center mb-5">
                    {Icon && <Icon className="w-5 h-5 text-pc-primary" />}
                  </div>
                  <h3 className="font-heading text-base font-bold text-pc-primary mb-3">
                    {model.title}
                  </h3>
                  <p className="font-body text-sm text-pc-muted leading-relaxed">
                    {model.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

// CTA
const CtaSection = () => (
  <section className="bg-pc-primary py-20 texture-grain">
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
    >
      <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-white tracking-tight mb-4">
        Need a Construction Estimate?
      </h2>
      <p className="font-body text-white/50 text-base max-w-lg mx-auto mb-8">
        Tell us about your project and we'll provide an honest, detailed estimate
        with no hidden costs.
      </p>
      <Link to="/contact">
        <Button className="bg-pc-accent hover:bg-pc-accent/90 text-white text-sm font-heading font-semibold px-8 h-12 rounded-lg transition-colors duration-200">
          Request a Quote
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </motion.div>
  </section>
);

export default function Services() {
  return (
    <main>
      <PageHeader />
      <ServiceDetails />
      <ContractModelsSection />
      <CtaSection />
    </main>
  );
}
